import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/YoutubeAnalyticsTagsTopRanking.graphql';
import { Query, QueryyoutubeAnalyticsTagsTopRankingArgs } from '../../__generated__/globalTypes';

export const useYoutubeAnalyticsTagsTopRankingQuery = (
  options?: QueryHookOptions<Query, QueryyoutubeAnalyticsTagsTopRankingArgs>
) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryyoutubeAnalyticsTagsTopRankingArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useYoutubeAnalyticsTagsTopRankingLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryyoutubeAnalyticsTagsTopRankingArgs>
): QueryResult<Query, QueryyoutubeAnalyticsTagsTopRankingArgs> & {
  getYoutubeAnalyticsTagsTopRanking: LazyQueryExecFunction<Query, QueryyoutubeAnalyticsTagsTopRankingArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getYoutubeAnalyticsTagsTopRanking, result] = useLazyQuery<Query, QueryyoutubeAnalyticsTagsTopRankingArgs>(
    QUERY,
    {
      onError: (error) => {
        enqueueSnackbar(t(error.message), { variant: 'error' });
      },
      ...options
    }
  );

  return { getYoutubeAnalyticsTagsTopRanking, ...result };
};

export const useYoutubeAnalyticsTagsTopRankingPromiseQuery = () => {
  const client = useApolloClient();

  const getYoutubeAnalyticsTagsTopRanking = useCallback(
    (options?: Omit<QueryOptions<QueryyoutubeAnalyticsTagsTopRankingArgs, Query>, 'query'>) =>
      client.query<Query, QueryyoutubeAnalyticsTagsTopRankingArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getYoutubeAnalyticsTagsTopRanking };
};
// Suspense Query Hooks
export const useYoutubeAnalyticsTagsTopRankingSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QueryyoutubeAnalyticsTagsTopRankingArgs>
) => {
  const result = useSuspenseQuery<Query, QueryyoutubeAnalyticsTagsTopRankingArgs>(QUERY, {
    ...options
  });

  return result;
};
